import { Popover, Space, Table, Tag } from 'antd';
import { useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import { EllipsisOutlined } from '@ant-design/icons';
import { useMemo } from 'react';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';

type AssignConversationInfos =
  StoreHomeV2.StoreConsultationDistribution['storeInfos'][number]['assignConversationInfos'];
type RoleInfo = AssignConversationInfos[number]['assignConversationRoleInfos'][number] & {
  userName: string;
  userId: number;
};

function ConsultationPopover(props: { dataSource: AssignConversationInfos }) {
  const { dataSource } = props;
  const { t } = useTranslate();
  const lang = useSystemLanguageValue();
  const content = useMemo(() => {
    let INTELLIGENT_ASSIGNMENT_COUNT = 0;
    let SUPERVISE_ALL_COUNT = 0;
    dataSource.forEach((item) => {
      if (item?.assignConversationRoleInfos?.[0]?.roleAssignModeEnum === 'INTELLIGENT_ASSIGNMENT') {
        INTELLIGENT_ASSIGNMENT_COUNT++;
      } else if (item?.assignConversationRoleInfos?.[0]?.roleAssignModeEnum === 'SUPERVISE_ALL') {
        SUPERVISE_ALL_COUNT++;
      }
    });

    const strs = [];
    if (INTELLIGENT_ASSIGNMENT_COUNT) {
      strs.push(
        t('department.x帐号', { x: INTELLIGENT_ASSIGNMENT_COUNT }) +
          (lang === 'zh-CN' ? '' : ' ') +
          t('department.智能分配'),
      );
    }

    if (SUPERVISE_ALL_COUNT) {
      strs.push(
        t('department.x帐号', { x: SUPERVISE_ALL_COUNT }) +
          (lang === 'zh-CN' ? '' : ' ') +
          t('department.监督全部'),
      );
    }

    return strs.length === 2 ? strs.join(' | ') : strs.join('');
  }, [dataSource, t, lang]);

  const tableDataSource = useMemo(() => {
    return dataSource
      .reduce((acc, item) => {
        acc = [
          ...acc,
          ...(item?.assignConversationRoleInfos?.map((role) => ({
            ...role,
            userName: item.userName,
            userId: item.userId,
          })) || []),
        ];

        return acc;
      }, [] as RoleInfo[])
      .filter((item) => item.roleAssignModeEnum !== 'NOT_ENGAGE_IN_ASSIGNMENT')
      .sort((a) => {
        return a.roleAssignModeEnum === 'INTELLIGENT_ASSIGNMENT' ? -1 : 1;
      });
  }, [dataSource]);

  const modeMap: any = {
    INTELLIGENT_ASSIGNMENT: t('department.智能分配'),
    SUPERVISE_ALL: t('department.监督全部'),
  } as const;

  return (
    <Popover
      getPopupContainer={(trigger) => trigger}
      content={
        <PreviewWrapper>
          <Table
            bordered={false}
            dataSource={tableDataSource}
            rowKey="userId"
            size="small"
            columns={[
              {
                key: 'userName',
                dataIndex: 'userName',
                title: t('department.帐号'),
                render: (text) => <TextEllipsis>{text}</TextEllipsis>,
              },
              {
                key: 'mode',
                title: `${t('department.角色')} | ${t('department.分配模式')}`,
                render: (_, record: RoleInfo) => {
                  const text =
                    (record.isDefault
                      ? t(`department.${record.roleName}` as any)
                      : record.roleName) +
                    ' | ' +
                    modeMap[record.roleAssignModeEnum];
                  return record.roleAssignModeEnum === 'INTELLIGENT_ASSIGNMENT' ? (
                    <Tag color="green">{text}</Tag>
                  ) : (
                    <BlueTag>{text}</BlueTag>
                  );
                },
              },
            ]}
            pagination={false}
          />
        </PreviewWrapper>
      }
    >
      <Space size={6} align="center">
        {content}
        <Ellipsis />
      </Space>
    </Popover>
  );
}

export const PreviewWrapper = styled.div`
  margin: -12px -16px;
  max-height: 280px;
  overflow: auto;
  padding: 16px;
  > div {
    background: #fff;
  }
`;

const Ellipsis = styled(EllipsisOutlined)`
  border-radius: 50%;
  background-color: #d6e8ff;
  padding: 2px;
  font-size: 10px;
  color: rgba(0, 0, 0, 0.4);
`;

const TextEllipsis = styled.div`
  overflow: hidden;
  max-width: 180px;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const BlueTag = styled(Tag)`
  color: var(--ant-primary-color);
  border-color: #d6e8ff;
`;

export default ConsultationPopover;
